import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Building2, Server, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { api } from '../api/client';
import { fmtTime } from '../lib/format';

type MyOrg = {
  id: string; name: string; inn: string | null;
  project_status: string | null; contract_active: boolean;
  contract_ends_at: string | null; tariff: string | null;
};
type MyInstallation = {
  id: string; organization_id: string;
  name: string | null; topology: string | null; os: string | null;
  versions: { component: string; current: string | null; branch?: string | null; installed_at?: string | null }[];
  tickets_open: number; tickets_total: number;
};
type Overview = {
  organization: MyOrg;
  installations: MyInstallation[];
  tickets_open: number;
  tickets_total: number;
  last_ticket_at: string | null;
};

const PROJECT_LABEL: Record<string, string> = {
  support: 'На поддержке', pilot: 'Пилот', no_support: 'Без поддержки',
};
const PROJECT_BADGE: Record<string, string> = {
  support: 'bg-emerald-900/40 text-emerald-300 border-emerald-700',
  pilot:   'bg-purple-900/40 text-purple-300 border-purple-700',
  no_support: 'bg-slate-800 text-slate-500 border-slate-700',
};

export default function MyOverviewPage() {
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get('/api/v1/my/overview')
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || e?.message || 'Ошибка'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader2 className="animate-spin text-blue-400 mx-auto mt-10" />;
  if (err) return (
    <div className="bg-red-900/30 border border-red-800 text-red-300 rounded p-4 max-w-xl mx-auto mt-10">
      {err}
    </div>
  );
  if (!data) return null;

  const o = data.organization;
  return (
    <section className="space-y-4 max-w-5xl">
      {/* Шапка */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
        <div className="flex items-center gap-3 mb-2">
          <Building2 className="text-blue-400" />
          <h1 className="text-2xl font-bold text-white">{o.name}</h1>
          {o.project_status && (
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${PROJECT_BADGE[o.project_status]}`}>
              {PROJECT_LABEL[o.project_status] || o.project_status}
            </span>
          )}
        </div>
        <div className="text-xs text-slate-400 grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          {o.inn && <div><span className="text-slate-500">ИНН:</span> <span className="text-white font-mono">{o.inn}</span></div>}
          <div>
            <span className="text-slate-500">Договор:</span>{' '}
            {o.contract_active
              ? <span className="text-emerald-400">активен</span>
              : <span className="text-red-400">не активен</span>}
            {o.contract_ends_at && (
              <span className="text-slate-600 text-[10px] ml-1">до {o.contract_ends_at.slice(0,10)}</span>
            )}
          </div>
          {o.tariff && <div><span className="text-slate-500">Тариф:</span> <span className="text-white">{o.tariff}</span></div>}
        </div>
      </div>

      {/* Сводка по тикетам */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Tile label="Открыто тикетов" value={data.tickets_open}
              icon={<AlertTriangle size={14} className="text-yellow-400" />}
              hint={data.tickets_open > 0 ? 'нужно следить' : 'всё под контролем'} />
        <Tile label="Всего тикетов" value={data.tickets_total}
              icon={<CheckCircle2 size={14} className="text-emerald-400" />} />
        <Tile label="Последний тикет"
              value={data.last_ticket_at ? fmtTime(data.last_ticket_at) : '—'}
              icon={<Server size={14} className="text-blue-400" />} />
      </div>

      <div className="flex justify-end">
        <Link to="/" className="text-xs text-blue-400 hover:text-blue-300">
          Все тикеты →
        </Link>
      </div>

      {/* Инсталляции */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl">
        <header className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
          <Server size={14} className="text-blue-400" />
          <h2 className="font-bold text-white">Ваши инсталляции</h2>
          <span className="text-[10px] text-slate-500">{data.installations.length}</span>
        </header>
        <div className="p-3 space-y-3">
          {data.installations.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">
              Инсталляций пока не зарегистрировано. Если у вас есть продукт — обратитесь к менеджеру.
            </div>
          ) : data.installations.map(inst => (
            <div key={inst.id} className="bg-slate-950/50 border border-slate-800 rounded-lg p-3">
              <div className="flex items-baseline justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-bold truncate">
                    {inst.name || inst.topology || '(инсталляция)'}
                  </div>
                  {inst.os && <div className="text-[11px] text-slate-500">{inst.os}</div>}
                </div>
                <div className="text-xs text-slate-400 whitespace-nowrap">
                  Тикетов: <span className="text-white font-bold">{inst.tickets_open}</span>
                  <span className="text-slate-600"> открыто / {inst.tickets_total} всего</span>
                </div>
              </div>
              {inst.versions.length > 0 && (
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5 text-[11px]">
                  {inst.versions.slice(0, 6).map((v, i) => (
                    <div key={i} className="bg-slate-900 border border-slate-800 rounded px-2 py-1 flex items-center justify-between">
                      <span className="text-slate-400">{v.component}</span>
                      <span className="text-white font-mono">{v.current}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Tile({ label, value, icon, hint }:
  { label: string; value: number | string; icon: React.ReactNode; hint?: string }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-2xl font-bold text-white mt-1">{value}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}
